import * as React from 'react';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import gettext from '@sdk/lang/gettext';
import SlingTip from '@sdk/common/slingTip/SlingTip';
import Text14 from '@sdk/components/ui/typography/Text14';
import type { ClientFlags, SaveClientFlags } from '@sdk/authentication/types';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';

import { TabType } from '../types';

const TipsContent = {
  [TabType.preferences]: {
    content: gettext(
      `Your preferences define the look and feel of your Sling account. They don’t affect other employees in your organization.`
    ),
    clientFlagKey: 'preferencesTipDismissed',
    forAdminsOnly: false,
  },
  [TabType.notifications]: {
    content: gettext(
      `Indicate which notifications you want to receive and how you wish to receive them.`
    ),
    clientFlagKey: 'notificationsTipDismissed',
    forAdminsOnly: false,
  },
  [TabType.companySettings]: {
    content: gettext(`Define which features and information are available to your employees.
    These settings apply to everyone within your company and can be managed only by Admins.`),
    clientFlagKey: 'companySettingsTipDismissed',
    forAdminsOnly: true,
  },
  [TabType.schedule]: {
    content: gettext(`Set up the schedule visibility, restrictions, and features for the company
    These settings apply to everyone within your company and can be managed only by Admins.`),
    clientFlagKey: 'scheduleTipDismissed',
    forAdminsOnly: true,
  },
  [TabType.timeClock]: {
    content: gettext(`Set up and customize the Time clock feature for your organization.
    These settings apply to everyone within your company and can be managed only by Admins.`),
    clientFlagKey: 'timeclockTipDismissed',
    forAdminsOnly: true,
  },
  [TabType.laborCost]: {
    content: gettext(`Configure labor cost and specify managers access to it.
    Only those who have access to labor cost can view or edit its data.`),
    clientFlagKey: 'laborCostTipDismissed',
    forAdminsOnly: true,
  },
  [TabType.timeOff]: {
    content: gettext(`Set rules around time off requests and paid time off.
    These settings apply to everyone within your company and can be managed only by Admins.`),
    clientFlagKey: 'timeOffsTipDismissed',
    forAdminsOnly: true,
  },
  [TabType.timeBlocks]: {
    content: gettext(
      'Make your schedules smarter! Create day parts to group and filter your schedules by time, and add time blocks to apply common shift times to shifts with one click.'
    ),
    clientFlagKey: 'timeBlocksTipDismissed',
    forAdminsOnly: true,
  },
  [TabType.integrations]: {
    content: gettext(
      `Integrate Sling with the platforms you already use to save time and money when scheduling and processing payroll.`
    ),
    clientFlagKey: 'integrationsTipDismissed',
    forAdminsOnly: true,
  },
};

type Props = {
  activeTab: TabType;
  saveClientFlags: SaveClientFlags;
  clientFlags: ClientFlags;
  currentUser: AuthUserType;
};

const SettingsTips: React.FC<React.PropsWithChildren<Props>> = ({
  activeTab,
  saveClientFlags,
  clientFlags,
  currentUser,
}) => {
  const onTipDismiss = (): void => {
    saveClientFlags({
      flags: {
        ['settingsTips']: {
          ...clientFlags.settingsTips,
          [TipsContent[activeTab].clientFlagKey]: true,
        },
      },
    });
  };

  if (TipsContent[activeTab].forAdminsOnly && !currentUser.isAdmin) {
    return null;
  }

  return (
    <SlingTip
      isVisible={!clientFlags.settingsTips[TipsContent[activeTab].clientFlagKey]}
      onDismiss={onTipDismiss}>
      <Text14 semiBold themeColor={ThemeColorType.white}>
        {TipsContent[activeTab].content}
      </Text14>
    </SlingTip>
  );
};

export default SettingsTips;
